// React
import { Fragment, useMemo } from "react"

// Router
import { useParams } from "@/lib/router"

// Queries
import {
	ProjectTypeEnum,
	StatsInformationByInterestPeriodType,
	useSingleProjectStatsInterestPeriodsQuery,
} from "@/api/graphql"

// UI
import { Card } from "@/components/Card"
import { Heading } from "@/components/Typography"

// Tables
import {
	useReactTable,
	flexRender,
	CellContext,
	createColumnHelper,
	getCoreRowModel,
} from "@/lib/table"
import {
	Table,
	TableBody,
	TableDataCell,
	TableRowCell,
	TableHead,
	TableHeading,
} from "@/components/table-controls/TableItems"

// I18n
import { useTrans } from "@/i18n"
import { useLang } from "@/context/lang"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"

/**
 * ProjectInterestPeriodsTable
 * @returns
 */
export function ProjectInterestPeriodsTable() {
	// I18n
	const t = useTrans("dashboard")
	const { formatCurrency } = useLang()

	// Dates
	const DateTime = useLocalizedDateTime()

	// Router
	const params = useParams()

	// Fetch
	const { data, isLoading } = useSingleProjectStatsInterestPeriodsQuery({
		id: String(params.id),
	})
	const currentProject = data?.projects?.at(0)

	// Memo
	const stats = useMemo(() => {
		return (
			(data?.project_statistics
				?.stats_information_by_interest_period as StatsInformationByInterestPeriodType[]) ||
			[]
		)
	}, [data?.project_statistics?.stats_information_by_interest_period])

	// Table
	const columnHelper =
		createColumnHelper<StatsInformationByInterestPeriodType>()
	const columns = useMemo(
		() => [
			columnHelper.accessor("end_date", {
				id: "interest_period",
				header: () => (
					<TableHeading variant="static">
						{t(
							"project:project.revenue.table.headers.interest_period",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell className="font-medium">
						{DateTime.fromISO(info.getValue()).year}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("production_per_share", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.production_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{info.getValue()
							? Number(info.getValue()).toFixed(2)
							: "-"}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("energy_price", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.energy_price",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{info.getValue()
							? formatCurrency(Number(info.getValue()), {
									minimumFractionDigits: 4,
								})
							: "-"}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("solar_interest_per_share", {
				header: () => (
					<TableHeading
						tooltip={t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.payment_per_share_tooltip",
						)}
					>
						{t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.solar_interest_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{info.getValue()
							? formatCurrency(Number(info.getValue()), {
									minimumFractionDigits: 2,
								})
							: "-"}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("payment_per_share", {
				header: () => (
					<TableHeading
						tooltip={t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.payment_per_share_tooltip",
						)}
					>
						{t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.payment_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{info.getValue()
							? formatCurrency(Number(info.getValue()), {
									minimumFractionDigits: 2,
								})
							: "-"}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("return_on_investment", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.return_on_investment",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{Number(info.getValue()).toFixed(2)} %
					</TableDataCell>
				),
			}),
			columnHelper.accessor("expected_production_per_share", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.expected_production_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{Number(info.getValue()).toFixed(2)}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("expected_solar_interest_per_share", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.expected_solar_interest_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{formatCurrency(Number(info.getValue()), {
							minimumFractionDigits: 2,
						})}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("expected_payment_per_share", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.expected_payment_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{formatCurrency(Number(info.getValue()), {
							minimumFractionDigits: 2,
						})}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("expected_return_on_investment", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:dashboard.stats.project_stats.table.interest_periods.expected_return_on_investment",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{Number(info.getValue()).toFixed(2)} %
					</TableDataCell>
				),
			}),
		],
		[],
	)
	const table = useReactTable({
		columns,
		data: stats,
		getCoreRowModel: getCoreRowModel(),
		enableHiding: true,
		state: {
			// Only show these columns if we have a solarShares project
			columnVisibility: {
				energy_price:
					currentProject?.type === ProjectTypeEnum.SolarShares,
				production_per_share:
					currentProject?.type === ProjectTypeEnum.SolarShares,
				expected_production_per_share:
					currentProject?.type === ProjectTypeEnum.SolarShares,

				// TODO: Re-enable these once calcs are fixed
				return_on_investment: false,
				expected_return_on_investment: false,
			},
		},
	})

	// Loading state
	if (isLoading) {
		return <ProjectInterestPeriodsTableLoading />
	}

	// Data state
	return (
		<Card className="row mt-5">
			<Heading as="h5" className="mb-4 text-gray-600">
				{t(
					"dashboard:dashboard.stats.project_stats.table.interest_periods.title",
				)}
			</Heading>
			<Table className="min-w-[64rem] lg:min-w-0" data-testid="tablebody">
				<TableHead>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRowCell key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								return (
									<Fragment key={header.id}>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</Fragment>
								)
							})}
						</TableRowCell>
					))}
				</TableHead>

				<TableBody
					data-testid="tablebody-overview"
					data-pageindex={table.getState().pagination.pageIndex}
				>
					{table.getRowModel().rows.map((row) => {
						const isOdd = row.index % 2 === 0
						return (
							<Fragment key={row.id}>
								<TableRowCell isOdd={isOdd}>
									{row
										.getVisibleCells()
										.map((cell, index) => {
											return (
												<Fragment key={index}>
													{flexRender(
														cell.column.columnDef
															.cell,
														cell.getContext(),
													)}
												</Fragment>
											)
										})}
								</TableRowCell>
							</Fragment>
						)
					})}
				</TableBody>
			</Table>
		</Card>
	)
}

/**
 * ProjectInterestPeriodsTableLoading
 * @returns
 */
export function ProjectInterestPeriodsTableLoading() {
	const t = useTrans("dashboard")

	return (
		<Card className="row mt-5">
			<Table className="min-w-[64rem] lg:min-w-0">
				<TableHead>
					<TableRowCell>
						<TableHeading>
							{t(
								"dashboard.stats.project_stats.table.interest_periods.period",
							)}
						</TableHeading>
						<TableHeading>
							{t(
								"dashboard.stats.project_stats.table.interest_periods.production_per_share",
							)}
						</TableHeading>
						<TableHeading>
							{t(
								"dashboard.stats.project_stats.table.interest_periods.energy_price",
							)}
						</TableHeading>
						<TableHeading>
							{t(
								"dashboard.stats.project_stats.table.interest_periods.solar_interest_per_share",
							)}
						</TableHeading>
						<TableHeading>
							{t(
								"dashboard.stats.project_stats.table.interest_periods.payment_per_share",
							)}
						</TableHeading>
						<TableHeading>
							{t(
								"dashboard.stats.project_stats.table.interest_periods.return_on_investment",
							)}
						</TableHeading>
						<TableHeading>
							{t(
								"dashboard.stats.project_stats.table.interest_periods.expected_production_per_share",
							)}
						</TableHeading>
						<TableHeading>
							{t(
								"dashboard.stats.project_stats.table.interest_periods.expected_solar_interest_per_share",
							)}
						</TableHeading>
						<TableHeading>
							{t(
								"dashboard.stats.project_stats.table.interest_periods.expected_payment_per_share",
							)}
						</TableHeading>
					</TableRowCell>
				</TableHead>
				<TableBody>
					{[...Array(5)].map((_, index) => (
						<TableRowCell key={index} isOdd={index % 2 === 0}>
							<TableDataCell>-</TableDataCell>
							<TableDataCell>-</TableDataCell>
							<TableDataCell>-</TableDataCell>
							<TableDataCell>-</TableDataCell>
							<TableDataCell>-</TableDataCell>
							<TableDataCell>-</TableDataCell>
							<TableDataCell>-</TableDataCell>
							<TableDataCell>-</TableDataCell>
							<TableDataCell>-</TableDataCell>
						</TableRowCell>
					))}
				</TableBody>
			</Table>
		</Card>
	)
}
